import { useState } from 'react';
import toast from 'react-hot-toast';
import { Search } from 'lucide-react';
import { SlideOver } from '../common/SlideOver';
import api, { coachMoveFlagsApi } from '../../lib/api';
import type { CoachMoveDiscoveryMethod, CoachMoveReason, PersonDetail } from '../../types';

const REASONS: Array<{ value: CoachMoveReason; label: string }> = [
  { value: 'LEFT', label: 'No longer at this school' },
  { value: 'RETIRED', label: 'Retired' },
  { value: 'MOVED', label: 'Moved to another school' },
  { value: 'WRONG_SCHOOL', label: 'Never was at this school (wrong link)' },
];

const METHODS: Array<{ value: CoachMoveDiscoveryMethod; label: string }> = [
  { value: 'NEW_COACH_DIRECT', label: 'I just talked to the new coach' },
  { value: 'COACH_DIRECT', label: 'Talked to the coach directly' },
  { value: 'SCHOOL_STAFF', label: 'Talked to the school / AD / front office' },
  { value: 'SCHOOL_VISIT', label: 'School visit — confirmed on site' },
  { value: 'UNREACHABLE', label: 'Tried their cell — disconnected / no answer' },
  { value: 'SECONDHAND', label: 'Heard it secondhand' },
  { value: 'HUNCH', label: 'Just my read (hunch)' },
];

interface OrgHit { id: string; name: string; city: string | null; state: string | null }

interface CoachNoLongerHereSheetProps {
  isOpen: boolean;
  onClose: () => void;
  person: PersonDetail;
  onSuccess: () => void;
  /** 'notify' = non-owner heads-up (Task to the assigned rep) instead of a real flag. */
  mode?: 'raise' | 'notify';
  notifyTargetName?: string;
  /** Prefill (owner reviewing a heads-up): seeds the form from the reporter's evidence. */
  initial?: {
    fromOrganizationId?: string;
    reason?: CoachMoveReason;
    discoveryMethod?: CoachMoveDiscoveryMethod;
    toOrganizationId?: string | null;
    toOrganizationName?: string | null;
    toSchoolText?: string | null;
    note?: string | null;
  };
}

export const CoachNoLongerHereSheet = ({ isOpen, onClose, person, onSuccess, mode = 'raise', notifyTargetName, initial }: CoachNoLongerHereSheetProps) => {
  const targetName = notifyTargetName ?? 'the assigned rep';
  const currentOrgs = person.organizations.filter((l) => !('endDate' in l) || (l as { endDate?: string | null }).endDate == null);
  const orgChoices = (currentOrgs.length > 0 ? currentOrgs : person.organizations)
    .map((l) => l.organization)
    .filter((o, i, all) => all.findIndex((x) => x.id === o.id) === i);

  // State initializers honor `initial` — the parent remounts the sheet per open
  // (key on isOpen) so prefill applies fresh each time.
  const [fromOrganizationId, setFromOrganizationId] = useState<string>(
    initial?.fromOrganizationId && orgChoices.some((o) => o.id === initial.fromOrganizationId)
      ? initial.fromOrganizationId
      : orgChoices[0]?.id ?? '',
  );
  const [reason, setReason] = useState<CoachMoveReason>(initial?.reason ?? 'LEFT');
  const [method, setMethod] = useState<CoachMoveDiscoveryMethod | ''>(initial?.discoveryMethod ?? '');
  const [destQuery, setDestQuery] = useState(initial?.toSchoolText ?? '');
  const [destResults, setDestResults] = useState<OrgHit[]>([]);
  const [dest, setDest] = useState<OrgHit | null>(
    initial?.toOrganizationId && initial?.toOrganizationName
      ? { id: initial.toOrganizationId, name: initial.toOrganizationName, city: null, state: null }
      : null,
  );
  const [note, setNote] = useState(initial?.note ?? '');
  const [busy, setBusy] = useState(false);

  const searchDest = async (q: string) => {
    setDestQuery(q);
    setDest(null);
    if (q.trim().length < 2) return setDestResults([]);
    try {
      const res = await api.get(`/api/organizations/search?q=${encodeURIComponent(q)}&limit=8`);
      setDestResults(res.data.organizations ?? []);
    } catch { setDestResults([]); }
  };

  const submit = async () => {
    if (!fromOrganizationId || !method) return;
    setBusy(true);
    try {
      const payload = {
        personId: person.id,
        fromOrganizationId,
        reason,
        discoveryMethod: method,
        toOrganizationId: dest?.id ?? null,
        toSchoolText: dest ? null : destQuery.trim() || null,
        note: note.trim() || null,
      };
      if (mode === 'notify') {
        await coachMoveFlagsApi.notify(payload);
        toast.success(`Sent to ${targetName}`);
      } else {
        const { flag } = await coachMoveFlagsApi.raise(payload);
        toast.success(
          flag.status === 'APPLIED'
            ? 'Record updated — thanks for the intel'
            : flag.status === 'ON_CALL_LIST'
              ? 'Reported — queued for a verification call'
              : 'Reported — sent for review',
        );
      }
      onSuccess();
      onClose();
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } } };
      toast.error(err.response?.data?.message || 'Failed to report');
    } finally {
      setBusy(false);
    }
  };

  return (
    <SlideOver
      isOpen={isOpen}
      onClose={onClose}
      title="Coach no longer here"
      footer={
        <button
          type="button"
          onClick={submit}
          disabled={busy || !fromOrganizationId || !method}
          className="btn-primary w-full justify-center disabled:opacity-50"
        >
          {busy
            ? mode === 'notify' ? 'Sending…' : 'Reporting…'
            : mode === 'notify' ? `Notify ${targetName}` : 'Report it'}
        </button>
      }
    >
      <div className="space-y-5">
        {mode === 'notify' && (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
            You're not the assigned rep — this sends your report to{' '}
            <span className="font-semibold">{targetName}</span>, who'll decide what happens next.
          </div>
        )}
        {orgChoices.length > 1 && (
          <div>
            <label className="label mb-1">Which school?</label>
            <select className="input" value={fromOrganizationId} onChange={(e) => setFromOrganizationId(e.target.value)}>
              {orgChoices.map((o) => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </select>
          </div>
        )}

        <div>
          <label className="label mb-1">What happened?</label>
          <div className="space-y-1.5">
            {REASONS.map((r) => (
              <label key={r.value} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <input type="radio" name="cmf-reason" checked={reason === r.value} onChange={() => setReason(r.value)} />
                {r.label}
              </label>
            ))}
          </div>
        </div>

        {reason === 'MOVED' && (
          <div>
            <label className="label mb-1">Where to? (if you know)</label>
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
              <input
                className="input pl-8"
                placeholder="Search schools…"
                value={dest ? `${dest.name}${dest.city ? ` — ${dest.city}, ${dest.state}` : ''}` : destQuery}
                onChange={(e) => searchDest(e.target.value)}
              />
            </div>
            {!dest && destResults.length > 0 && (
              <ul className="mt-1 card divide-y divide-gray-100 dark:divide-gray-700 max-h-48 overflow-y-auto">
                {destResults.map((o) => (
                  <li key={o.id}>
                    <button
                      type="button"
                      className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-700"
                      onClick={() => { setDest(o); setDestResults([]); }}
                    >
                      {o.name}
                      {o.city && <span className="text-gray-400"> — {o.city}, {o.state}</span>}
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {!dest && destQuery.trim().length >= 2 && destResults.length === 0 && (
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Not in the CRM — we'll record "{destQuery.trim()}" for the review team.
              </p>
            )}
          </div>
        )}

        <div>
          <label className="label mb-1">How do you know?</label>
          <div className="space-y-1.5">
            {METHODS.map((m) => (
              <label key={m.value} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <input type="radio" name="cmf-method" checked={method === m.value} onChange={() => setMethod(m.value)} />
                {m.label}
              </label>
            ))}
          </div>
        </div>

        <div>
          <label className="label mb-1">Note (optional)</label>
          <textarea className="input" rows={2} maxLength={2000} value={note} onChange={(e) => setNote(e.target.value)} />
        </div>
      </div>
    </SlideOver>
  );
};
